import {
  QuestionMarkCircleIcon,
  DocumentTextIcon,
  CpuChipIcon,
  PencilIcon,
  EyeIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  ChartBarIcon,
  PlayIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline'

export default function QuizBuilderHelp() {
  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="flex items-center mb-6">
          <QuestionMarkCircleIcon className="h-8 w-8 text-green-600 mr-3" />
          <h1 className="text-3xl font-bold text-gray-900">Quiz Builder</h1>
        </div>

        <div className="prose prose-lg max-w-none">
          <p className="text-gray-600 mb-8">
            The Quiz Builder allows you to create comprehensive assessments for your training courses.
            Generate questions manually or use AI assistance to create engaging quizzes with multiple difficulty levels.
          </p>

          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Key Features</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-gray-50 rounded-lg p-6">
              <div className="flex items-center mb-3">
                <DocumentTextIcon className="h-6 w-6 text-blue-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Training Integration</h3>
              </div>
              <p className="text-gray-600 text-sm">
                Link quizzes directly to training documents and track learner progress through assessments.
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-6">
              <div className="flex items-center mb-3">
                <CpuChipIcon className="h-6 w-6 text-purple-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">AI Question Generation</h3>
              </div>
              <p className="text-gray-600 text-sm">
                Automatically generate questions based on training content using advanced AI algorithms.
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-6">
              <div className="flex items-center mb-3">
                <PencilIcon className="h-6 w-6 text-green-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Manual Creation</h3>
              </div>
              <p className="text-gray-600 text-sm">
                Create custom questions manually with full control over content and difficulty.
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-6">
              <div className="flex items-center mb-3">
                <ChartBarIcon className="h-6 w-6 text-orange-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Difficulty Levels</h3>
              </div>
              <p className="text-gray-600 text-sm">
                Set different difficulty levels (Easy, Medium, Hard) and manage multiple quiz versions.
              </p>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Quiz Creation Methods</h2>

          <div className="space-y-6 mb-8">
            <div className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center mb-4">
                <PencilIcon className="h-6 w-6 text-blue-600 mr-3" />
                <h3 className="text-lg font-semibold text-gray-900">Manual Quiz Creation</h3>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <h4 className="font-medium text-gray-900 mb-2">Question Builder Interface</h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Question Text</label>
                    <textarea
                      className="w-full p-2 border border-gray-300 rounded-md text-sm opacity-75 cursor-not-allowed"
                      placeholder="Enter your question here..."
                      rows="2"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Option A</label>
                      <input
                        type="text"
                        className="w-full p-2 border border-gray-300 rounded-md text-sm opacity-75 cursor-not-allowed"
                        placeholder="First option"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Option B</label>
                      <input
                        type="text"
                        className="w-full p-2 border border-gray-300 rounded-md text-sm opacity-75 cursor-not-allowed"
                        placeholder="Second option"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Option C</label>
                      <input
                        type="text"
                        className="w-full p-2 border border-gray-300 rounded-md text-sm opacity-75 cursor-not-allowed"
                        placeholder="Third option"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Option D</label>
                      <input
                        type="text"
                        className="w-full p-2 border border-gray-300 rounded-md text-sm opacity-75 cursor-not-allowed"
                        placeholder="Fourth option"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Correct Answer</label>
                    <select className="w-full p-2 border border-gray-300 rounded-md text-sm opacity-75 cursor-not-allowed">
                      <option>Select correct answer</option>
                      <option>A</option>
                      <option>B</option>
                      <option>C</option>
                      <option>D</option>
                    </select>
                  </div>

                  <div className="flex justify-end">
                    <button className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm opacity-75 cursor-not-allowed">
                      Add Question
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center mb-4">
                <CpuChipIcon className="h-6 w-6 text-purple-600 mr-3" />
                <h3 className="text-lg font-semibold text-gray-900">AI-Generated Quizzes</h3>
              </div>

              <div className="bg-purple-50 rounded-lg p-4 mb-4">
                <h4 className="font-medium text-purple-900 mb-2">AI Generation Settings</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-purple-700 mb-1">Number of Questions</label>
                    <select className="w-full p-2 border border-purple-300 rounded-md text-sm opacity-75 cursor-not-allowed">
                      <option>5</option>
                      <option>10</option>
                      <option>15</option>
                      <option>20</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-purple-700 mb-1">Difficulty Level</label>
                    <select className="w-full p-2 border border-purple-300 rounded-md text-sm opacity-75 cursor-not-allowed">
                      <option>Easy</option>
                      <option>Medium</option>
                      <option>Hard</option>
                    </select>
                  </div>
                  <div className="flex items-end">
                    <button className="w-full px-4 py-2 bg-purple-600 text-white rounded-md text-sm opacity-75 cursor-not-allowed">
                      Generate Quiz
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Quiz Management</h2>

          <div className="bg-gray-50 rounded-lg p-6 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Quiz Dashboard</h3>

            <div className="space-y-4">
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="font-medium text-gray-900">Safety Training Quiz</h4>
                    <p className="text-sm text-gray-600">Easy • 10 Questions</p>
                  </div>
                  <div className="flex space-x-2">
                    <button className="px-3 py-1 bg-blue-100 text-blue-800 rounded-md text-sm opacity-75 cursor-not-allowed">
                      <EyeIcon className="h-4 w-4 inline mr-1" />
                      View
                    </button>
                    <button className="px-3 py-1 bg-green-100 text-green-800 rounded-md text-sm opacity-75 cursor-not-allowed">
                      <CheckCircleIcon className="h-4 w-4 inline mr-1" />
                      Set Current
                    </button>
                    <button className="px-3 py-1 bg-red-100 text-red-800 rounded-md text-sm opacity-75 cursor-not-allowed">
                      <TrashIcon className="h-4 w-4 inline mr-1" />
                      Delete
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div className="text-center">
                    <div className="font-semibold text-gray-900">15</div>
                    <div className="text-gray-600">Attempts</div>
                  </div>
                  <div className="text-center">
                    <div className="font-semibold text-green-600">87%</div>
                    <div className="text-gray-600">Avg Score</div>
                  </div>
                  <div className="text-center">
                    <div className="font-semibold text-blue-600">12</div>
                    <div className="text-gray-600">Passed</div>
                  </div>
                  <div className="text-center">
                    <div className="font-semibold text-red-600">3</div>
                    <div className="text-gray-600">Failed</div>
                  </div>
                </div>
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="font-medium text-gray-900">Advanced Compliance Quiz</h4>
                    <p className="text-sm text-gray-600">Hard • 20 Questions</p>
                  </div>
                  <div className="flex space-x-2">
                    <button className="px-3 py-1 bg-blue-100 text-blue-800 rounded-md text-sm opacity-75 cursor-not-allowed">
                      <EyeIcon className="h-4 w-4 inline mr-1" />
                      View
                    </button>
                    <button className="px-3 py-1 bg-gray-100 text-gray-800 rounded-md text-sm opacity-75 cursor-not-allowed">
                      <CheckCircleIcon className="h-4 w-4 inline mr-1" />
                      Current
                    </button>
                    <button className="px-3 py-1 bg-red-100 text-red-800 rounded-md text-sm opacity-75 cursor-not-allowed">
                      <TrashIcon className="h-4 w-4 inline mr-1" />
                      Delete
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div className="text-center">
                    <div className="font-semibold text-gray-900">8</div>
                    <div className="text-gray-600">Attempts</div>
                  </div>
                  <div className="text-center">
                    <div className="font-semibold text-yellow-600">73%</div>
                    <div className="text-gray-600">Avg Score</div>
                  </div>
                  <div className="text-center">
                    <div className="font-semibold text-blue-600">5</div>
                    <div className="text-gray-600">Passed</div>
                  </div>
                  <div className="text-center">
                    <div className="font-semibold text-red-600">3</div>
                    <div className="text-gray-600">Failed</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-gray-900 mb-4">How to Use</h2>

          <div className="space-y-6 mb-8">
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-4">
                <span className="text-green-600 font-semibold text-sm">1</span>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Select Training Document</h4>
                <p className="text-gray-600 text-sm">Choose the training course you want to create a quiz for.</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-4">
                <span className="text-green-600 font-semibold text-sm">2</span>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Choose Creation Method</h4>
                <p className="text-gray-600 text-sm">Select manual creation or AI generation based on your needs.</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-4">
                <span className="text-green-600 font-semibold text-sm">3</span>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Set Difficulty & Parameters</h4>
                <p className="text-gray-600 text-sm">Configure question count, difficulty level, and other quiz settings.</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-4">
                <span className="text-green-600 font-semibold text-sm">4</span>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Create Questions</h4>
                <p className="text-gray-600 text-sm">Build questions manually or let AI generate them automatically.</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-4">
                <span className="text-green-600 font-semibold text-sm">5</span>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Review & Publish</h4>
                <p className="text-gray-600 text-sm">Review the quiz, set it as current, and make it available to learners.</p>
              </div>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Best Practices</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <div className="flex items-start">
                <CheckCircleIcon className="h-6 w-6 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-blue-900 mb-2">Question Design</h4>
                  <ul className="text-blue-800 text-sm space-y-1">
                    <li>• Use clear, unambiguous language</li>
                    <li>• Ensure one definitive correct answer</li>
                    <li>• Avoid trick questions</li>
                    <li>• Test questions before publishing</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <div className="flex items-start">
                <CheckCircleIcon className="h-6 w-6 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-green-900 mb-2">Assessment Strategy</h4>
                  <ul className="text-green-800 text-sm space-y-1">
                    <li>• Match difficulty to learner level</li>
                    <li>• Include various question types</li>
                    <li>• Set appropriate passing scores</li>
                    <li>• Provide feedback on incorrect answers</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <div className="flex items-start">
              <ArrowPathIcon className="h-6 w-6 text-yellow-600 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-yellow-900 mb-2">AI Generation Tips</h4>
                <ul className="text-yellow-800 text-sm space-y-1">
                  <li>• Review AI-generated questions for accuracy</li>
                  <li>• Ensure questions align with learning objectives</li>
                  <li>• Balance question difficulty across the quiz</li>
                  <li>• Customize AI prompts for better results</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}